(() => {
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const SCALE_STEP = 0.25;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class CTProductZoom {
    constructor(modal) {
      this.modal = modal;
      this.controls = modal.querySelector('[data-ct-product-zoom-controls]');
      this.content = modal.querySelector('.product-media-modal__content');
      this.zoomOutButton = modal.querySelector('[data-ct-product-zoom-out]');
      this.zoomInButton = modal.querySelector('[data-ct-product-zoom-in]');
      this.resetButton = modal.querySelector('[data-ct-product-zoom-reset]');
      this.level = modal.querySelector('[data-ct-product-zoom-level]');
      this.previousButton = modal.querySelector('[data-ct-product-zoom-previous]');
      this.nextButton = modal.querySelector('[data-ct-product-zoom-next]');
      this.count = modal.querySelector('[data-ct-product-zoom-count]');

      if (!this.controls || !this.content || !this.zoomOutButton || !this.zoomInButton || !this.resetButton || !this.level) return;

      this.scale = MIN_SCALE;
      this.x = 0;
      this.y = 0;
      this.image = null;
      this.pointers = new Map();
      this.dragPointerId = null;
      this.dragStart = null;
      this.pinchStartDistance = 0;
      this.pinchStartScale = MIN_SCALE;

      this.zoomOutButton.addEventListener('click', () => this.setScale(this.scale - SCALE_STEP));
      this.zoomInButton.addEventListener('click', () => this.setScale(this.scale + SCALE_STEP));
      this.resetButton.addEventListener('click', () => this.reset());
      this.previousButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.navigate(-1);
      });
      this.nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.navigate(1);
      });
      this.previousButton?.addEventListener('pointerup', (event) => event.stopImmediatePropagation());
      this.nextButton?.addEventListener('pointerup', (event) => event.stopImmediatePropagation());

      modal.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
      modal.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      modal.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.content.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.content.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.controls.addEventListener('pointerup', (event) => event.stopPropagation());
      modal.addEventListener('dblclick', (event) => this.onDoubleClick(event));
      modal.addEventListener('keydown', (event) => this.onKeyDown(event));

      this.openObserver = new MutationObserver(() => this.onOpenStateChange());
      this.openObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
      this.onOpenStateChange();
    }

    getActiveImage() {
      return (
        this.modal.querySelector('.product-media-modal__content img.active[data-media-id]') ||
        this.modal.querySelector('.product-media-modal__content img[data-media-id]')
      );
    }

    getMediaItems() {
      return [...this.content.children].filter((item) => item.matches('[data-media-id]'));
    }

    getActiveMedia() {
      return this.content.querySelector(':scope > .active[data-media-id]') || this.getMediaItems()[0] || null;
    }

    syncActiveMedia() {
      const activeMedia = this.getActiveMedia();
      const image = activeMedia?.matches('img') ? activeMedia : activeMedia?.querySelector('img[data-media-id]');
      this.image = image || null;
      this.controls.hidden = !this.image;
      this.updateNavigation();
    }

    navigate(direction) {
      const items = this.getMediaItems();
      if (items.length < 2) return;

      const current = this.getActiveMedia();
      const currentIndex = Math.max(0, items.indexOf(current));
      const nextIndex = (currentIndex + direction + items.length) % items.length;

      this.reset(false);
      items.forEach((item, index) => item.classList.toggle('active', index === nextIndex));
      this.syncActiveMedia();
      this.render();
    }

    updateNavigation() {
      const items = this.getMediaItems();
      const currentIndex = Math.max(0, items.indexOf(this.getActiveMedia()));
      const hasMultiple = items.length > 1;

      if (this.previousButton) this.previousButton.disabled = !hasMultiple;
      if (this.nextButton) this.nextButton.disabled = !hasMultiple;
      if (this.count) {
        this.count.hidden = !hasMultiple;
        this.count.value = hasMultiple ? `${currentIndex + 1} / ${items.length}` : '';
        this.count.textContent = this.count.value;
      }
    }

    onOpenStateChange() {
      if (!this.modal.hasAttribute('open')) {
        this.reset(false);
        this.controls.hidden = true;
        return;
      }

      requestAnimationFrame(() => {
        this.syncActiveMedia();
        this.reset(false);
      });
    }

    setImage(image) {
      if (!image || image === this.image) return;
      this.reset(false);
      this.image = image;
      this.controls.hidden = false;
      this.render();
    }

    setScale(nextScale) {
      if (!this.image) return;
      this.scale = clamp(Math.round(nextScale * 100) / 100, MIN_SCALE, MAX_SCALE);

      if (this.scale === MIN_SCALE) {
        this.x = 0;
        this.y = 0;
      } else {
        this.clampPosition();
      }

      this.render();
    }

    reset(shouldFocus = false) {
      if (this.image) this.image.style.transform = '';
      this.scale = MIN_SCALE;
      this.x = 0;
      this.y = 0;
      this.pointers.clear();
      this.dragPointerId = null;
      this.dragStart = null;
      this.pinchStartDistance = 0;
      this.modal.classList.remove('ct-product-zoom--zoomed', 'ct-product-zoom--dragging');
      this.updateControls();
      if (shouldFocus) this.resetButton.focus();
    }

    clampPosition() {
      if (!this.image) return;
      const maxX = (this.image.clientWidth * (this.scale - 1)) / 2;
      const maxY = (this.image.clientHeight * (this.scale - 1)) / 2;
      this.x = clamp(this.x, -maxX, maxX);
      this.y = clamp(this.y, -maxY, maxY);
    }

    render() {
      if (!this.image) return;
      this.clampPosition();
      this.image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      this.modal.classList.toggle('ct-product-zoom--zoomed', this.scale > MIN_SCALE);
      this.updateControls();
    }

    updateControls() {
      if (!this.level) return;
      this.level.value = `${Math.round(this.scale * 100)}%`;
      this.level.textContent = this.level.value;
      this.zoomOutButton.disabled = this.scale <= MIN_SCALE;
      this.zoomInButton.disabled = this.scale >= MAX_SCALE;
      this.resetButton.disabled = this.scale <= MIN_SCALE;
    }

    imageFromEvent(event) {
      const image = event.target instanceof Element ? event.target.closest('img[data-media-id]') : null;
      return image && this.modal.contains(image) ? image : null;
    }

    onWheel(event) {
      const image = this.imageFromEvent(event);
      if (!image) return;
      event.preventDefault();
      this.setImage(image);
      this.setScale(this.scale + (event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP));
    }

    onDoubleClick(event) {
      const image = this.imageFromEvent(event);
      if (!image) return;
      event.preventDefault();
      this.setImage(image);
      this.setScale(this.scale > MIN_SCALE ? MIN_SCALE : 2);
    }

    onKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigate(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigate(1);
      }
    }

    onPointerDown(event) {
      const image = this.imageFromEvent(event);
      if (!image || event.button > 0) return;

      this.setImage(image);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      image.setPointerCapture?.(event.pointerId);

      if (this.pointers.size === 2) {
        const points = [...this.pointers.values()];
        this.pinchStartDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        this.pinchStartScale = this.scale;
      } else if (this.scale > MIN_SCALE) {
        this.dragPointerId = event.pointerId;
        this.dragStart = { pointerX: event.clientX, pointerY: event.clientY, x: this.x, y: this.y };
        this.modal.classList.add('ct-product-zoom--dragging');
      }

      if (event.pointerType === 'touch' || this.scale > MIN_SCALE) event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.pinchStartDistance > 0) {
        const points = [...this.pointers.values()];
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        this.setScale(this.pinchStartScale * (distance / this.pinchStartDistance));
        event.preventDefault();
        return;
      }

      if (event.pointerId !== this.dragPointerId || !this.dragStart || this.scale <= MIN_SCALE) return;
      this.x = this.dragStart.x + event.clientX - this.dragStart.pointerX;
      this.y = this.dragStart.y + event.clientY - this.dragStart.pointerY;
      this.render();
      event.preventDefault();
    }

    onPointerUp(event) {
      const isZoomTarget = this.imageFromEvent(event) || (event.target instanceof Element && event.target.closest('[data-ct-product-zoom-controls]'));
      if (!isZoomTarget) return;

      this.pointers.delete(event.pointerId);
      if (event.pointerId === this.dragPointerId) {
        this.dragPointerId = null;
        this.dragStart = null;
        this.modal.classList.remove('ct-product-zoom--dragging');
      }

      if (this.pointers.size < 2) {
        this.pinchStartDistance = 0;
        this.pinchStartScale = this.scale;
      }

      event.stopImmediatePropagation();
    }
  }

  const initialize = (root = document) => {
    root.querySelectorAll?.('product-modal').forEach((modal) => {
      if (modal.dataset.ctProductZoomReady === 'true') return;
      modal.dataset.ctProductZoomReady = 'true';
      new CTProductZoom(modal);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
